import React from 'react';
import { Alert, Card, CardContent, Chip, Grid, Stack, Typography } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { BieMoney } from './BieValue';

type Props = {
  kpi?: any;
  cards?: any;
  constraints?: any[];
};

const get = (obj: any, keys: string[], fallback = 0) => {
  for (const k of keys) {
    if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return fallback;
};

class SmartSimulatorResultsPanel extends React.PureComponent<Props> {
  resultStatus() {
    const data = this.props.kpi || this.props.cards || {};
    const partial = Number(get(data, ['partialresult', 'resultatpartiel', 'respartval'], 0));
    const constraints = this.props.constraints || [];
    const hasCritical = constraints.some((x: any) =>
      ['ERROR', 'CRITICAL', 'HIGH'].includes(String(x.severity || x.issuecode || '').toUpperCase())
    );

    if (hasCritical) return { label: 'Review Needed', color: 'warning' as const, icon: <WarningAmberIcon /> };
    if (partial < 0) return { label: 'Negative Result', color: 'error' as const, icon: <TrendingDownIcon /> };
    return { label: 'Viable Scenario', color: 'success' as const, icon: <CheckCircleIcon /> };
  }

  renderMetric(label: string, value: any, tone?: 'good' | 'bad') {
    return (
      <Grid size={{ xs: 12, sm: 6 }} key={label}>
        <Card elevation={0} variant="outlined" sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                color: tone === 'good' ? 'success.main' : tone === 'bad' ? 'error.main' : 'text.primary',
              }}
            >
              <BieMoney value={value} />
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    );
  }

  render() {
    const data = this.props.kpi || this.props.cards || {};
    const constraints = this.props.constraints || [];
    const status = this.resultStatus();

    const sales = get(data, ['sales', 'ventes', 'venthydval']);
    const purchases = get(data, ['purchases', 'achats', 'achathydval']);
    const opex = get(data, ['opex']);
    const salaries = get(data, ['salaries', 'salaires']);
    const partial = get(data, ['partialresult', 'resultatpartiel', 'respartval']);
    const capacity = get(data, ['capacity', 'economiccapacity', 'ecp']);

    return (
      <Card elevation={1} sx={{ height: '100%' }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <div>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Simulator Results
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Live what-if outcome from the current driver mix
                </Typography>
              </div>
              <Chip icon={status.icon} label={status.label} color={status.color} />
            </Stack>

            <Grid container spacing={1.5}>
              {this.renderMetric('Sales', sales)}
              {this.renderMetric('Purchases', purchases)}
              {this.renderMetric('OPEX', opex)}
              {this.renderMetric('Salaries', salaries)}
              {this.renderMetric('Partial Result', partial, Number(partial) >= 0 ? 'good' : 'bad')}
              {this.renderMetric('Economic Capacity', capacity)}
            </Grid>

            {constraints.length > 0 ? (
              <Stack spacing={1}>
                {constraints.slice(0, 3).map((x: any, idx: number) => (
                  <Alert severity={(x.severity || 'warning').toLowerCase() as any} key={x.constraintcode || idx}>
                    {x.message || x.constraintname || JSON.stringify(x)}
                  </Alert>
                ))}
              </Stack>
            ) : (
              <Alert severity="success">No major simulator constraint alerts detected.</Alert>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  }
}

export default SmartSimulatorResultsPanel;
